import React from 'react';
import { usePopper } from 'react-popper';
import styled, { keyframes, DefaultTheme, useTheme } from 'styled-components/macro';
import { SPACINGS } from '../../utils/style';
import { Paragraph, Text, ActionText } from '../../typography';
import {
  IconX,
  IconMail,
  IconPhone,
  IconWrapper,
  IconUserPhoto,
} from '../../icons';



const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const glow = (props: DefaultTheme) => keyframes`
  0%   { box-shadow: 0 0 2px ${props.colors.primary};}
  50%  { box-shadow: 0 0 12px ${props.colors.primary};}
  100% { box-shadow: 0 0 2px ${props.colors.primary};}
`;

const PopperWrapper = styled.div`
  opacity: 0;
  animation: ${fadeIn} 0.1s ease-in 0.1s forwards;
  z-index: ${(p) => p.theme.zIndexes.overlay};
`;

const PopupWrapper = styled.div<{ type: 'client' | 'fa' }>`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-right: ${SPACINGS[8]};
  min-width: 400px;
  max-width: 600px;
  padding: ${SPACINGS[8]};
  text-align: start;
  box-shadow: ${(p) => p.theme.boxShadows.base};
  background-color: ${(p) => p.theme.colors.white};
`;

const FloatingIconWrapper = styled.div`
  position: fixed;
  bottom: 50px;
  right: 50px;
  width: 70px;
  height: 70px;
  border-radius: 50%;
`;

const FloatingIcon = styled.div<{ type: 'client' | 'fa' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  box-shadow: ${(p) => p.theme.boxShadows.sm};
  border: 2px solid ${(p) => p.theme.colors.primary};
  cursor: pointer;
  background-color: ${(p) => p.theme.colors.primary90w};
`;

const Glowing = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: 50%;
  background-color: ${(p) => p.theme.colors.primary};
  animation: ${(p) => glow(p.theme)} 2.8s infinite;
  z-index: -1;
`;

const Face = styled.div<{ url: string }>`
  background: url(${(p) => p.url}) center center;
  background-size: cover;
  width: 100%;
  height: 100%;
  border-radius: 50%;
`;

type ContactPopupBaseProps = {
  title: string;
  description: string;
  email: string;
  phone?: string;
  label?: string;
};

type ContactPopupFaProps = {
  type: 'fa';
  imageUrl: string;
};

type ContactPopupClientProps = {
  type: 'client';
};

// the type means that is showing that user type
// so in the FA app we will have type="client"
type ContactPopupProps = ContactPopupBaseProps &
  (ContactPopupFaProps | ContactPopupClientProps);

function ContactPopup(props: ContactPopupProps) {
  const { label, title, description, email, phone } = props;
  const theme = useTheme();

  const [referenceElement, setReferenceElement] = React.useState(null as any);
  const [popperElement, setPopperElement] = React.useState(null as any);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'top-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 20],
        },
      },
      {
        name: 'flip',
        enabled: false,
      },
      {
        name: 'preventOverflow',
        enabled: true,
      },
      {
        name: 'hide',
        enabled: false,
      },
    ],
  });

  const [isVisible, setIsVisible] = React.useState(false);

  let icon = null;
  if (props.type === 'fa' && props.imageUrl) {
    icon = <Face url={props.imageUrl} />;
  } else {
    icon = isVisible ? <IconX /> : <IconUserPhoto />;
  }

  return (
    <>
      <FloatingIconWrapper aria-label={label}>
        <FloatingIcon
          type={props.type}
          onClick={() => setIsVisible((b) => !b)}
          ref={setReferenceElement}
        >
          {icon}
        </FloatingIcon>
        {isVisible && <Glowing />}
      </FloatingIconWrapper>
      {isVisible && (
        <PopperWrapper
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          <PopupWrapper type={props.type}>
            <IconWrapper
              style={{
                cursor: 'pointer',
                position: 'absolute',
                top: SPACINGS[8],
                right: SPACINGS[8],
              }}
              hoverColor= {theme.colors.transparent}
              onClick={() => setIsVisible(false)}
            >
              <IconX />
            </IconWrapper>
            <Text size="xl" weight="bold">
              <span dangerouslySetInnerHTML={{ __html: title }}></span>
            </Text>
            <Paragraph style={{ marginBottom: SPACINGS[6] }}>
              {description}
            </Paragraph>
            <span>
              <ActionText type="link-external" to={`mailto:${email}`}>
                <IconWrapper style={{ marginRight: SPACINGS[1] }} hoverColor= {theme.colors.transparent}>
                  <IconMail stroke={theme.colors.primary}/>
                </IconWrapper>
                {email}
              </ActionText>
            </span>
            {phone && (
              <span>
                <ActionText type="link-external" to={`tel:${phone}`}>
                  <IconWrapper style={{ marginRight: SPACINGS[1] }} hoverColor= {theme.colors.transparent} >
                    <IconPhone stroke={theme.colors.primary}/>
                  </IconWrapper>
                  {phone}
                </ActionText>
              </span>
            )}
          </PopupWrapper>
        </PopperWrapper>
      )}
    </>
  );
}

export default ContactPopup;
